/**
 * ETL Service API
 *
 * ETL calls go to /etl-api/{customerId}/* which the main backend proxies to
 * the correct customer's ETL service container.
 *
 * Customer management calls go to /api/etl-customers/*.
 */
import axios from 'axios';
import { apiClient } from '../../../services/api/client';
import { supabase } from '../../../lib/supabase';

// ── Customer-scoped ETL API factory ─────────────────────────────────────────
// Usage: const api = createCustomerETLApi('ethen'); api.getHealth();
//
// /etl-api/* is proxied by the main backend to the customer's ETL container and
// requires the same Supabase bearer token as every other authenticated endpoint.

export function createCustomerETLApi(customerId) {
  const base = `/etl-api/${customerId}`;
  const client = axios.create({ timeout: 30000, headers: { 'Content-Type': 'application/json' } });

  client.interceptors.request.use(async (config) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch (e) {
      console.error('Failed to attach auth token to ETL request', e);
    }
    return config;
  });

  return {
    // Health & Status
    getHealth: () => client.get(`${base}/health`).then(r => r.data),
    getStatus: () => client.get(`${base}/status`).then(r => r.data),

    // Job Management
    triggerRun: (options = {}) => client.post(`${base}/etl/trigger`, options).then(r => r.data),
    getJobs: (limit = 20) => client.get(`${base}/etl/jobs`, { params: { limit } }).then(r => r.data),
    getJob: (jobId) => client.get(`${base}/etl/jobs/${jobId}`).then(r => r.data),

    // Schedule
    getSchedule: () => client.get(`${base}/etl/schedule`).then(r => r.data),
    setSchedule: (payload) => client.put(`${base}/etl/schedule`, payload).then(r => r.data),

    // OCEL files produced by this customer's ETL
    getOCELFiles: () => client.get(`${base}/etl/ocel-files`).then(r => r.data),

    // Table discovery
    discoverTables: (connection) =>
      client.post(`${base}/dlt/discover-tables`, { connection }).then(r => r.data),

    // Warehouse (DuckDB)
    listWarehouseTables: (schema = 'erp') =>
      client.get(`${base}/warehouse/tables`, { params: { schema } }).then(r => r.data),
    getTableColumns: (tableName, schema = 'erp') =>
      client
        .get(`${base}/warehouse/tables/${encodeURIComponent(tableName)}/columns`, { params: { schema } })
        .then(r => r.data),
    queryWarehouse: (sql) => client.post(`${base}/warehouse/query`, { sql }).then(r => r.data),
    exportWarehouse: (tables, schema = 'erp') =>
      client
        .post(`${base}/warehouse/export`, { tables, schema }, { responseType: 'blob' })
        .then(r => r.data),

    // ERP SQL
    executeERPQuery: (sql) => client.post(`${base}/execute-query`, { sql }).then(r => r.data),

    // Transformer config
    getTransformerConfig: () => client.get(`${base}/etl/transformer-config`).then(r => r.data),
    saveTransformerConfig: (payload) =>
      client.put(`${base}/etl/transformer-config`, payload).then(r => r.data),
    getExtractionSourceAliases: () =>
      client.get(`${base}/etl/extraction-source-aliases`).then(r => r.data),

    // Step-by-step pipeline
    triggerStep: (stepId, options = {}) =>
      client.post(`${base}/etl/steps/${stepId}/run`, options).then(r => r.data),
    getAllStepsStatus: () => client.get(`${base}/etl/steps/status/all`).then(r => r.data),
    getStepStatus: (stepId) => client.get(`${base}/etl/steps/${stepId}/status`).then(r => r.data),
    getStepHistory: (stepId, limit = 20) =>
      client.get(`${base}/etl/steps/${stepId}/history`, { params: { limit } }).then(r => r.data),
    pollStepRun: (stepRunId) =>
      client.get(`${base}/etl/steps/poll/${stepRunId}`).then(r => r.data),
    downloadStepCsv: (stepId) =>
      client
        .get(`${base}/etl/steps/${stepId}/download-csv`, { responseType: 'blob' })
        .then(r => r.data),
  };
}

// ── Customer management (main backend /api/etl-customers) ───────────────────

export const getCustomers = () =>
  apiClient.get('/etl-customers').then(r => r.data);

export const getCustomerStatus = (customerId) =>
  apiClient.get(`/etl-customers/${customerId}/status`).then(r => r.data);

export const connectCustomer = (customerId) =>
  apiClient.post(`/etl-customers/${customerId}/connect`).then(r => r.data);

export const disconnectCustomer = (customerId) =>
  apiClient.post(`/etl-customers/${customerId}/disconnect`).then(r => r.data);

// ── Backend file manager (unchanged — goes to main backend /api) ─────────────

export const getAvailableOCELFiles = () =>
  apiClient.get('/ocel/files').then(r => r.data);

export const getActiveOCELFile = () =>
  apiClient.get('/ocel/active-file').then(r => r.data);

export const setActiveOCELFile = (fileType) =>
  apiClient.post('/ocel/active-file', { file_type: fileType }).then(r => r.data);

export const refreshETLFiles = () =>
  apiClient.post('/ocel/files/refresh').then(r => r.data);

// Delegates to the shared, in-flight-coalesced implementation so concurrent
// callers across the app share one /ocel/metadata request per file_type.
export { getOCELMetadata } from '../../../services/api/ocel.api';
